import React, { useState, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { FileText, CheckCircle, Calendar, Phone, Download, Users, X, Send, User, Mail, MapPin, GraduationCap, Building, Baby, Heart } from 'lucide-react'
import useAdmissionStore from '../stores/admissionStore'
import { getImportantDates } from '../api/importantDateService'

const Admissions = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  // Get admission store actions
  const addApplication = useAdmissionStore(state => state.addApplication)

  // Important Dates State
  const [importantDates, setImportantDates] = useState([])
  const [loadingDates, setLoadingDates] = useState(true)

  // Fetch important dates from API
  useEffect(() => {
    const fetchImportantDates = async () => {
      try {
        setLoadingDates(true)
        const response = await getImportantDates({ isActive: true })
        if (response.status === 'success' && response.data?.importantDates) {
          setImportantDates(response.data.importantDates)
        }
      } catch (error) {
        console.error('Error fetching important dates:', error)
        // Fallback to default dates if API fails
        setImportantDates([
          {
            event: "Admission Form Release",
            date: "March 1, 2024",
            status: "completed"
          },
          {
            event: "Last Date for Application",
            date: "April 30, 2024",
            status: "upcoming"
          },
          {
            event: "Document Verification",
            date: "May 1-15, 2024",
            status: "upcoming"
          },
          {
            event: "Admission Results",
            date: "May 20, 2024",
            status: "upcoming"
          }
        ])
      } finally {
        setLoadingDates(false)
      }
    }

    fetchImportantDates()
  }, [])

  // Form Modal State
  const [showAdmissionForm, setShowAdmissionForm] = useState(false)
  const [formStep, setFormStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    // Student Information
    studentName: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    aadharNumber: '',
    
    // Academic Information
    previousSchool: '',
    classApplying: '',
    academicYear: '2024-25',
    medium: '',
    
    // Parent/Guardian Information
    fatherName: '',
    fatherOccupation: '',
    fatherPhone: '',
    motherName: '',
    motherOccupation: '',
    motherPhone: '',
    guardianEmail: '',
    
    // Address Information
    address: '',
    city: '',
    state: 'Gujarat',
    pincode: '',
    
    // Additional Information
    specialNeeds: '',
    previousSchoolResult: '',
    extracurricular: ''
  })
  const [formErrors, setFormErrors] = useState({})

  // Form Validation
  const validateStep = (step) => {
    const errors = {}
    
    if (step === 1) {
      if (!formData.studentName.trim()) errors.studentName = 'Student name is required'
      if (!formData.dateOfBirth) errors.dateOfBirth = 'Date of birth is required'
      if (formData.dateOfBirth && new Date(formData.dateOfBirth) >= new Date()) {
        errors.dateOfBirth = 'Date of birth must be in the past'
      }
      if (!formData.gender) errors.gender = 'Gender is required'
      if (!formData.aadharNumber.trim()) errors.aadharNumber = 'Aadhar number is required'
      if (formData.aadharNumber && !/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, ''))) {
        errors.aadharNumber = 'Invalid Aadhar number format'
      }
    }
    
    if (step === 2) {
      if (!formData.classApplying) errors.classApplying = 'Class applying for is required'
      if (!formData.medium) errors.medium = 'Medium of instruction is required'
      if (!formData.previousSchool.trim()) errors.previousSchool = 'Previous school is required'
    }
    
    if (step === 3) {
      if (!formData.fatherName.trim()) errors.fatherName = 'Father\'s name is required'
      if (!formData.fatherPhone.trim()) errors.fatherPhone = 'Father\'s phone is required'
      if (formData.fatherPhone && !/^[6-9]\d{9}$/.test(formData.fatherPhone)) {
        errors.fatherPhone = 'Phone must be 10 digits starting with 6-9'
      }
      if (!formData.motherName.trim()) errors.motherName = 'Mother\'s name is required'
      if (!formData.guardianEmail.trim()) errors.guardianEmail = 'Email is required'
      if (formData.guardianEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guardianEmail)) {
        errors.guardianEmail = 'Invalid email format'
      }
    }
    
    if (step === 4) {
      if (!formData.address.trim()) errors.address = 'Address is required'
      if (!formData.city.trim()) errors.city = 'City is required'
      if (!formData.state.trim()) errors.state = 'State is required'
      if (!formData.pincode.trim()) errors.pincode = 'Pincode is required'
      if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
        errors.pincode = 'Invalid pincode format'
      }
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Form Handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleNextStep = () => {
    if (validateStep(formStep)) {
      setFormStep(prev => Math.min(prev + 1, 4))
    }
  }

  const handlePrevStep = () => {
    setFormStep(prev => Math.max(prev - 1, 1))
  }

  const handleFormSubmit = async () => {
    if (!validateStep(4)) return
    
    setIsSubmitting(true)
    try {
      // Transform form data to match the backend expected format
      // Map the form values to backend enum values
      const gradeMapping = {
        'nursery': 'Nursery',
        'lkg': 'LKG',
        'ukg': 'UKG',
        '1': 'Grade 1',
        '2': 'Grade 2',
        '3': 'Grade 3',
        '4': 'Grade 4',
        '5': 'Grade 5',
        '6': 'Grade 6',
        '7': 'Grade 7',
        '8': 'Grade 8',
        '9': 'Grade 9',
        '10': 'Grade 10'
      };
      
      const applicationData = {
        // Student Information (required)
        studentName: formData.studentName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        bloodGroup: formData.bloodGroup,
        aadharNumber: formData.aadharNumber.replace(/\s/g, ''), // Remove all spaces
        
        // Academic Information (required)
        gradeAppliedFor: gradeMapping[formData.classApplying] || formData.classApplying, // Map to backend enum values
        previousSchool: formData.previousSchool,
        academicYear: formData.academicYear,
        
        // Parent Information (required)
        fatherName: formData.fatherName,
        fatherOccupation: formData.fatherOccupation,
        fatherPhone: formData.fatherPhone,
        motherName: formData.motherName,
        motherOccupation: formData.motherOccupation,
        motherPhone: formData.motherPhone || '',
        
        // Address Information (required as nested object)
        address: {
          street: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        }
      }

      // Debug: Log the data being sent
      console.log('Submitting admission data:', applicationData)
      console.log('Full application data:', JSON.stringify(applicationData, null, 2))

      // Call the store action which calls the backend API
      const result = await addApplication(applicationData)
      
      console.log('Submission result:', result)
      
      if (result.success) {
        // Show success message
        alert(`Admission form submitted successfully! 
Application Number: ${result.data.applicationNumber || 'Pending'}
We will contact you within 2-3 working days.`)
        
        // Reset form
        setShowAdmissionForm(false)
        setFormStep(1)
        setFormData({
          studentName: '', dateOfBirth: '', gender: '', bloodGroup: '', aadharNumber: '',
          previousSchool: '', classApplying: '', academicYear: '2024-25', medium: '',
          fatherName: '', fatherOccupation: '', fatherPhone: '', motherName: '', motherOccupation: '', motherPhone: '', guardianEmail: '',
          address: '', city: '', state: 'Gujarat', pincode: '',
          specialNeeds: '', previousSchoolResult: '', extracurricular: ''
        })
        setFormErrors({})
      } else {
        // Show error message
        alert(`Error: ${result.message || 'Failed to submit application. Please try again.'}`)
      }
    } catch (error) {
      console.error('Form submission error:', error)
      alert('Error submitting form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const admissionSteps = [
    {
      step: 1,
      icon: <FileText className="w-8 h-8" />,
      title: t('admissions.steps.applicationForm'),
      description: t('admissions.steps.applicationFormDesc')
    },
    {
      step: 2,
      icon: <CheckCircle className="w-8 h-8" />,
      title: t('admissions.steps.documentVerification'),
      description: t('admissions.steps.documentVerificationDesc')
    },
    {
      step: 3,
      icon: <Users className="w-8 h-8" />,
      title: t('admissions.steps.interaction'),
      description: t('admissions.steps.interactionDesc')
    },
    {
      step: 4,
      icon: <CheckCircle className="w-8 h-8" />,
      title: t('admissions.steps.admissionConfirmation'),
      description: t('admissions.steps.admissionConfirmationDesc')
    }
  ]

  const requiredDocuments = [
    t('admissions.documents.birthCertificate'),
    t('admissions.documents.transferCertificate'),
    t('admissions.documents.academicRecords'),
    t('admissions.documents.aadharCard'),
    t('admissions.documents.photographs'),
    t('admissions.documents.casteCertificate'),
    t('admissions.documents.incomeCertificate')
  ]

  // State for fee structure toggle
  const [selectedFeeType, setSelectedFeeType] = useState('boys')

  const feeStructureBoys = [
    {
      level: "STD 5",
      schoolFee: "₹4,000",
      hostelFee: "₹12,500",
      total: "₹16,500",
      yearly: "₹33,000"
    },
    {
      level: "STD 6",
      schoolFee: "₹5,000",
      hostelFee: "₹12,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 7",
      schoolFee: "₹5,000",
      hostelFee: "₹12,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 8",
      schoolFee: "₹6,000",
      hostelFee: "₹15,000",
      total: "₹21,000",
      yearly: "₹42,000"
    },
    {
      level: "STD 9",
      schoolFee: "₹8,000",
      hostelFee: "₹15,000",
      total: "₹21,000",
      yearly: "₹42,000"
    },
    {
      level: "STD 10",
      schoolFee: "₹8,000",
      hostelFee: "₹17,500",
      total: "₹25,500",
      yearly: "₹51,000"
    },
    {
      level: "11-12 Com",
      schoolFee: "₹9,000",
      hostelFee: "₹18,000",
      total: "₹27,500",
      yearly: "₹55,000"
    },
    {
      level: "11-12 Sci",
      schoolFee: "₹18,000",
      hostelFee: "₹18,000",
      total: "₹36,000",
      yearly: "₹72,000"
    }
  ]

  const feeStructureGirls = [
    {
      level: "STD 5",
      schoolFee: "₹4,000",
      hostelFee: "₹10,500",
      total: "₹14,500",
      yearly: "₹29,000"
    },
    {
      level: "STD 6",
      schoolFee: "₹5,000",
      hostelFee: "₹10,500",
      total: "₹15,500",
      yearly: "₹31,000"
    },
    {
      level: "STD 7",
      schoolFee: "₹5,000",
      hostelFee: "₹10,500",
      total: "₹15,500",
      yearly: "₹31,000"
    },
    {
      level: "STD 8",
      schoolFee: "₹6,000",
      hostelFee: "₹11,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 9",
      schoolFee: "₹6,000",
      hostelFee: "₹11,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 10",
      schoolFee: "₹8,000",
      hostelFee: "₹14,500",
      total: "₹22,500",
      yearly: "₹45,000"
    },
    {
      level: "11-12 Com",
      schoolFee: "₹9,500",
      hostelFee: "₹14,500",
      total: "₹24,000",
      yearly: "₹48,000"
    },
    {
      level: "11-12 Sci",
      schoolFee: "₹18,000",
      hostelFee: "₹14,500",
      total: "₹32,500",
      yearly: "₹65,000"
    }
  ]

  const currentFeeStructure = selectedFeeType === 'boys' ? feeStructureBoys : feeStructureGirls

  return (
    <section id="admissions" className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-primary-purple font-poppins mb-4">
              {t('admissions.title')}
            </h2>
            <div className="w-24 h-1 bg-primary-yellow mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('admissions.description')}
            </p>
          </div>

          {/* Admission Process */}
          <div className="mb-16">
            <h3 className="text-2xl lg:text-3xl font-bold text-center text-primary-purple mb-12 font-montserrat">
              {t('admissions.process')}
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {admissionSteps.map((step, index) => (
                <div key={index} className="text-center">
                  {/* Step Number */}
                  <div className="relative mb-6">
                    <div className="w-16 h-16 bg-primary-purple rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                      {step.step}
                    </div>
                    {index < admissionSteps.length - 1 && (
                      <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-primary-purple/30"></div>
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className="text-primary-purple mb-4 flex justify-center">
                    {step.icon}
                  </div>
                  
                  {/* Content */}
                  <h4 className="text-lg font-semibold text-primary-purple mb-3">
                    {step.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Required Documents */}
            <div className="bg-gradient-to-br from-primary-purple/5 to-primary-blue/5 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-primary-purple mb-6 flex items-center">
                <FileText className="w-6 h-6 mr-3" />
                {t('admissions.documents.title')}
              </h3>
              <div className="space-y-3">
                {requiredDocuments.map((doc, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-primary-green" />
                    <span className="text-gray-700">{doc}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <button 
                  onClick={() => setShowAdmissionForm(true)}
                  className="w-full bg-primary-purple text-white py-3 rounded-lg hover:bg-primary-magenta transition-colors duration-300 font-semibold flex items-center justify-center space-x-2"
                >
                  <Download className="w-5 h-5" />
                  <span>Fill Admission Form</span>
                </button>
              </div>
            </div>

            {/* Important Dates */}
            <div className="bg-gradient-to-br from-primary-green/5 to-primary-yellow/5 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-primary-purple mb-6 flex items-center">
                <Calendar className="w-6 h-6 mr-3" />
                {t('admissions.importantDates.title')}
              </h3>
              <div className="space-y-4">
                {importantDates.map((date, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
                    <div>
                      <h4 className="font-semibold text-gray-800">{date.event}</h4>
                      <p className="text-gray-600 text-sm">{date.date}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      date.status === 'completed' 
                        ? 'bg-primary-green/20 text-primary-green' 
                        : 'bg-primary-yellow/20 text-primary-brown'
                    }`}>
                      {date.status === 'completed' ? t('admissions.importantDates.completed') : t('admissions.importantDates.upcoming')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Fee Structure */}
          <div className="mb-16">
            <h3 className="text-2xl lg:text-3xl font-bold text-center text-primary-purple mb-8 font-montserrat">
              Hostel Fee Structure
            </h3>
            
            {/* Toggle Switch */}
            <div className="flex justify-center mb-8">
              <div className="bg-gray-100 p-1 rounded-lg flex">
                <button
                  onClick={() => setSelectedFeeType('boys')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    selectedFeeType === 'boys'
                      ? 'bg-primary-purple text-white shadow-lg'
                      : 'text-gray-600 hover:text-primary-purple'
                  }`}
                >
                  {t('admissions.feeStructure.boys')}
                </button>
                <button
                  onClick={() => setSelectedFeeType('girls')}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    selectedFeeType === 'girls'
                      ? 'bg-primary-purple text-white shadow-lg'
                      : 'text-gray-600 hover:text-primary-purple'
                  }`}
                >
                  {t('admissions.feeStructure.girls')}
                </button>
              </div>
            </div>

            {/* Fee Structure Header */}
            <div className="text-center mb-6">
              <h4 className="text-xl font-bold text-primary-purple mb-2">
                {t('admissions.feeStructure.trustName')}
              </h4>
              <h5 className="text-lg font-semibold text-gray-700 mb-2">
                {t('admissions.feeStructure.schoolName')}
              </h5>
              <p className="text-sm text-gray-600">
                {selectedFeeType === 'boys' ? t('admissions.feeStructure.feeYearBoys') : t('admissions.feeStructure.feeYearGirls')}
              </p>
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
                <thead className="bg-primary-purple text-white">
                  <tr>
                    <th className="px-4 py-4 text-left font-semibold">{t('admissions.feeStructure.tableHeaders.std')}</th>
                    <th className="px-4 py-4 text-center font-semibold">{t('admissions.feeStructure.tableHeaders.schoolFee')}</th>
                    <th className="px-4 py-4 text-center font-semibold">{t('admissions.feeStructure.tableHeaders.hostelFee')}</th>
                    <th className="px-4 py-4 text-center font-semibold">{t('admissions.feeStructure.tableHeaders.total')}</th>
                    <th className="px-4 py-4 text-center font-semibold">{t('admissions.feeStructure.tableHeaders.yearly')}</th>
                  </tr>
                </thead>
                <tbody>
                  {currentFeeStructure.map((fee, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-4 py-4 font-semibold text-primary-purple">{fee.level}</td>
                      <td className="px-4 py-4 text-center">{fee.schoolFee}</td>
                      <td className="px-4 py-4 text-center">{fee.hostelFee}</td>
                      <td className="px-4 py-4 text-center font-semibold text-primary-purple">{fee.total}</td>
                      <td className="px-4 py-4 text-center font-bold text-primary-green">{fee.yearly}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {currentFeeStructure.map((fee, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <div className="text-center mb-4">
                    <h4 className="text-lg font-bold text-primary-purple">{fee.level}</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">{t('admissions.feeStructure.schoolFee')}:</span>
                      <span className="font-semibold">{fee.schoolFee}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">{t('admissions.feeStructure.hostelFee')}:</span>
                      <span className="font-semibold">{fee.hostelFee}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-primary-purple font-bold">{t('admissions.feeStructure.total')}:</span>
                      <span className="font-bold text-primary-purple">{fee.total}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 bg-primary-green/10 rounded-lg px-3">
                      <span className="text-primary-green font-bold">{t('admissions.feeStructure.yearly')}:</span>
                      <span className="font-bold text-primary-green text-lg">{fee.yearly}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 text-center space-y-2">
              <p className="text-gray-600 text-sm">
                *{t('admissions.feeStructure.disclaimers.feesSubjectToChange')}
              </p>
              <p className="text-gray-600 text-sm">
                *{t('admissions.feeStructure.disclaimers.firstTermNote')}
              </p>
              <p className="text-primary-purple font-semibold text-sm">
                {t('admissions.feeStructure.disclaimers.contactForLatest')}
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-primary-purple to-primary-blue rounded-2xl p-8 lg:p-12 text-white text-center">
            <h3 className="text-2xl lg:text-3xl font-bold mb-6">
              {t('admissions.feeStructure.admissionHelp.title')}
            </h3>
            <p className="text-lg mb-8 opacity-90">
              {t('admissions.feeStructure.admissionHelp.subtitle')}
            </p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <Phone className="w-8 h-8 mx-auto mb-3" />
                <h4 className="font-semibold mb-2">{t('admissions.feeStructure.admissionHelp.callUs')}</h4>
                <p>+91 9687685102 / +91 9687685108</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6">
                <FileText className="w-8 h-8 mx-auto mb-3" />
                <h4 className="font-semibold mb-2">{t('admissions.feeStructure.admissionHelp.email')}</h4>
                <p>mandavadsankul1996@gmail.com</p>
              </div>
              <div className="bg-white/10 rounded-lg p-6">
                <Calendar className="w-8 h-8 mx-auto mb-3" />
                <h4 className="font-semibold mb-2">{t('admissions.feeStructure.admissionHelp.admissionHelpline')}</h4>
                <p>+91 9723231955 / +91 7567363050</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admission Form Modal */}
      {showAdmissionForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h3 className="text-2xl font-bold text-primary-purple">Admission Application Form</h3>
                <p className="text-gray-600 mt-1">Step {formStep} of 4</p>
              </div>
              <button 
                onClick={() => setShowAdmissionForm(false)}
                className="text-gray-500 hover:text-gray-700 p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="px-6 py-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary-purple h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(formStep / 4) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-sm text-gray-600">
                <span>Student Info</span>
                <span>Academic</span>
                <span>Parents</span>
                <span>Address</span>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6">
              {/* Step 1: Student Information */}
              {formStep === 1 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-purple mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Student Information
                  </h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
                      <input
                        type="text"
                        value={formData.studentName}
                        onChange={(e) => handleInputChange('studentName', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.studentName ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter student's full name"
                      />
                      {formErrors.studentName && <p className="text-red-500 text-sm mt-1">{formErrors.studentName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Date of Birth *</label>
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.dateOfBirth ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {formErrors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{formErrors.dateOfBirth}</p>}
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Gender *</label>
                      <select
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.gender ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      {formErrors.gender && <p className="text-red-500 text-sm mt-1">{formErrors.gender}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Blood Group</label>
                      <select
                        value={formData.bloodGroup}
                        onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                      >
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Aadhar Number *</label>
                    <input
                      type="text"
                      value={formData.aadharNumber}
                      onChange={(e) => handleInputChange('aadharNumber', e.target.value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 '))}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.aadharNumber ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="XXXX XXXX XXXX"
                      maxLength="14"
                    />
                    {formErrors.aadharNumber && <p className="text-red-500 text-sm mt-1">{formErrors.aadharNumber}</p>}
                  </div>
                </div>
              )}

              {/* Step 2: Academic Information */}
              {formStep === 2 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-purple mb-4 flex items-center">
                    <GraduationCap className="w-5 h-5 mr-2" />
                    Academic Information
                  </h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Class Applying For *</label>
                      <select
                        value={formData.classApplying}
                        onChange={(e) => handleInputChange('classApplying', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.classApplying ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select Class</option>
                        <option value="nursery">Nursery</option>
                        <option value="lkg">LKG</option>
                        <option value="ukg">UKG</option>
                        <option value="1">Class 1</option>
                        <option value="2">Class 2</option>
                        <option value="3">Class 3</option>
                        <option value="4">Class 4</option>
                        <option value="5">Class 5</option>
                        <option value="6">Class 6</option>
                        <option value="7">Class 7</option>
                        <option value="8">Class 8</option>
                        <option value="9">Class 9</option>
                        <option value="10">Class 10</option>
                        <option value="11">Class 11</option>
                        <option value="12">Class 12</option>
                      </select>
                      {formErrors.classApplying && <p className="text-red-500 text-sm mt-1">{formErrors.classApplying}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Academic Year</label>
                      <input
                        type="text"
                        value={formData.academicYear}
                        onChange={(e) => handleInputChange('academicYear', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                        placeholder="2024-25"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Medium of Instruction *</label>
                    <select
                      value={formData.medium}
                      onChange={(e) => handleInputChange('medium', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.medium ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">Select Medium</option>
                      <option value="english">English</option>
                      <option value="gujarati">Gujarati</option>
                      <option value="hindi">Hindi</option>
                    </select>
                    {formErrors.medium && <p className="text-red-500 text-sm mt-1">{formErrors.medium}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Previous School *</label>
                    <input
                      type="text"
                      value={formData.previousSchool}
                      onChange={(e) => handleInputChange('previousSchool', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.previousSchool ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter previous school name (or 'First Time' if new admission)"
                    />
                    {formErrors.previousSchool && <p className="text-red-500 text-sm mt-1">{formErrors.previousSchool}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Previous Academic Result</label>
                    <input
                      type="text"
                      value={formData.previousSchoolResult}
                      onChange={(e) => handleInputChange('previousSchoolResult', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                      placeholder="Enter percentage/grade (if applicable)"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Parent/Guardian Information */}
              {formStep === 3 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-purple mb-4 flex items-center">
                    <Heart className="w-5 h-5 mr-2" />
                    Parent/Guardian Information
                  </h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Father's Name *</label>
                      <input
                        type="text"
                        value={formData.fatherName}
                        onChange={(e) => handleInputChange('fatherName', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.fatherName ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter father's full name"
                      />
                      {formErrors.fatherName && <p className="text-red-500 text-sm mt-1">{formErrors.fatherName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Father's Occupation</label>
                      <input
                        type="text"
                        value={formData.fatherOccupation}
                        onChange={(e) => handleInputChange('fatherOccupation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                        placeholder="Enter father's occupation"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Father's Phone Number *</label>
                    <input
                      type="tel"
                      value={formData.fatherPhone}
                      onChange={(e) => handleInputChange('fatherPhone', e.target.value.replace(/\D/g, ''))}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.fatherPhone ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter 10-digit phone number"
                      maxLength="10"
                    />
                    {formErrors.fatherPhone && <p className="text-red-500 text-sm mt-1">{formErrors.fatherPhone}</p>}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mother's Name *</label>
                      <input
                        type="text"
                        value={formData.motherName}
                        onChange={(e) => handleInputChange('motherName', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.motherName ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter mother's full name"
                      />
                      {formErrors.motherName && <p className="text-red-500 text-sm mt-1">{formErrors.motherName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mother's Occupation</label>
                      <input
                        type="text"
                        value={formData.motherOccupation}
                        onChange={(e) => handleInputChange('motherOccupation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                        placeholder="Enter mother's occupation"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mother's Phone Number</label>
                      <input
                        type="tel"
                        value={formData.motherPhone}
                        onChange={(e) => handleInputChange('motherPhone', e.target.value.replace(/\D/g, ''))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                        placeholder="Enter 10-digit phone number"
                        maxLength="10"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Email Address *</label>
                      <input
                        type="email"
                        value={formData.guardianEmail}
                        onChange={(e) => handleInputChange('guardianEmail', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.guardianEmail ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter email address"
                      />
                      {formErrors.guardianEmail && <p className="text-red-500 text-sm mt-1">{formErrors.guardianEmail}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Address & Additional Information */}
              {formStep === 4 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-purple mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Address & Additional Information
                  </h4>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Complete Address *</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.address ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter complete address"
                      rows="3"
                    />
                    {formErrors.address && <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>}
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">City *</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.city ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter city"
                      />
                      {formErrors.city && <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">State *</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.state ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter state"
                      />
                      {formErrors.state && <p className="text-red-500 text-sm mt-1">{formErrors.state}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Pincode *</label>
                      <input
                        type="text"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, ''))}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent ${formErrors.pincode ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter pincode"
                        maxLength="6"
                      />
                      {formErrors.pincode && <p className="text-red-500 text-sm mt-1">{formErrors.pincode}</p>}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Special Needs/Medical Conditions</label>
                    <textarea
                      value={formData.specialNeeds}
                      onChange={(e) => handleInputChange('specialNeeds', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                      placeholder="Please mention any special needs, allergies, or medical conditions"
                      rows="2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Extracurricular Interests</label>
                    <textarea
                      value={formData.extracurricular}
                      onChange={(e) => handleInputChange('extracurricular', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                      placeholder="Student's interests in sports, arts, music, etc."
                      rows="2"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200">
              <button
                onClick={handlePrevStep}
                disabled={formStep === 1}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex space-x-3">
                {formStep < 4 ? (
                  <button
                    onClick={handleNextStep}
                    className="px-6 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-magenta transition-colors duration-300"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleFormSubmit}
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-magenta transition-colors duration-300 flex items-center space-x-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Submit Application</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default Admissions